import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Alert,
  Paper,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useLanguage } from '../../i18n/LanguageContext';
import { Friend, Exercise, WorkoutShareExercise, FetchWithAuth } from '../../types';
import { API_URL } from '../../utils';
import { ExerciseAutocomplete } from '../common';

interface ShareWorkoutDialogProps {
  open: boolean;
  onClose: () => void;
  friends: Friend[];
  fetchWithAuth: FetchWithAuth;
}

interface ExerciseInput {
  exercise_id: number;
  exercise_name: string;
  sets: number;
  reps: number;
}

const ShareWorkoutDialog: React.FC<ShareWorkoutDialogProps> = ({
  open,
  onClose,
  friends,
  fetchWithAuth,
}) => {
  const { t } = useLanguage();
  const [selectedFriendId, setSelectedFriendId] = useState<number | ''>('');
  const [message, setMessage] = useState('');
  const [exerciseInputs, setExerciseInputs] = useState<ExerciseInput[]>([
    { exercise_id: 0, exercise_name: '', sets: 1, reps: 1 }
  ]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [availableExercises, setAvailableExercises] = useState<Exercise[]>([]);
  const [loadingExercises, setLoadingExercises] = useState(false);

  const acceptedFriends = friends.filter(f => f.status === 'accepted');

  const loadExercises = useCallback(async () => {
    setLoadingExercises(true);
    try {
      const response = await fetchWithAuth(`${API_URL}/exercises`);
      if (response.ok) {
        const data = await response.json();
        setAvailableExercises(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error loading exercises:', error);
    } finally {
      setLoadingExercises(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    if (open) {
      // Load exercises when dialog opens
      loadExercises();
    } else {
      // Reset form when dialog closes
      setSelectedFriendId('');
      setMessage('');
      setExerciseInputs([{ exercise_id: 0, exercise_name: '', sets: 1, reps: 1 }]);
      setError(null);
    }
  }, [open, loadExercises]);

  const handleCreateExercise = async (name: string): Promise<Exercise | null> => {
    try {
      const response = await fetchWithAuth(`${API_URL}/exercises`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      
      if (response.ok) {
        const newExercise = await response.json();
        setAvailableExercises([...availableExercises, newExercise]);
        return newExercise;
      }
    } catch (error) {
      console.error('Error creating exercise:', error);
    }
    return null;
  };

  const handleAddExercise = () => {
    setExerciseInputs([...exerciseInputs, { exercise_id: 0, exercise_name: '', sets: 1, reps: 1 }]);
  };

  const handleRemoveExercise = (index: number) => {
    if (exerciseInputs.length > 1) {
      setExerciseInputs(exerciseInputs.filter((_, i) => i !== index));
    }
  };

  const handleExerciseChange = (index: number, field: keyof ExerciseInput, value: string | number) => {
    const newExercises = [...exerciseInputs];
    if (field === 'exercise_name') {
      newExercises[index][field] = value as string;
    } else {
      newExercises[index][field] = value as number;
    }
    setExerciseInputs(newExercises);
  };

  const validateExercises = (): boolean => {
    for (const exercise of exerciseInputs) {
      if (!exercise.exercise_name.trim() || exercise.sets <= 0 || exercise.reps <= 0) {
        return false;
      }
    }
    return true;
  };

  const handleShare = async () => {
    setError(null);

    // Validation
    if (!selectedFriendId) {
      setError(t('workoutShare.selectFriend'));
      return;
    }

    if (!validateExercises()) {
      setError(t('workoutShare.fillAllFields'));
      return;
    }

    setLoading(true);

    try {
      // Convert exercises to the expected format
      const exercisesToShare: WorkoutShareExercise[] = exerciseInputs.map((ex, index) => ({
        exercise_name: ex.exercise_name.trim(),
        sets: ex.sets,
        reps: ex.reps,
        order_index: index
      }));

      const response = await fetchWithAuth(`${API_URL}/workout-shares`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          friendId: selectedFriendId,
          exercises: exercisesToShare,
          message: message.trim() || undefined
        }),
      });

      const data = await response.json();

      if (data.success) {
        onClose();
      } else {
        setError(data.message || t('workoutShare.shareError'));
      }
    } catch (err) {
      console.error('Error sharing workout:', err);
      setError(t('workoutShare.shareError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('workoutShare.title')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {acceptedFriends.length === 0 ? (
            <Alert severity="info">{t('workoutShare.noFriendsToShare')}</Alert>
          ) : (
            <>
              {/* Friend selection */}
              <TextField
                select
                fullWidth
                label={t('workoutShare.selectFriend')}
                value={selectedFriendId}
                onChange={(e) => setSelectedFriendId(Number(e.target.value))}
                disabled={loading}
              >
                {acceptedFriends.map((friend) => (
                  <MenuItem key={friend.id} value={friend.friend_id}>
                    {friend.friend_name}
                  </MenuItem>
                ))}
              </TextField>

              {/* Exercises */}
              <Typography variant="h6" sx={{ mt: 2 }}>
                {t('workoutShare.selectExercises')}
              </Typography>

              {exerciseInputs.map((exercise, index) => (
                <Paper key={index} sx={{ p: 2, backgroundColor: 'grey.50' }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, sm: 5 }}>
                      <ExerciseAutocomplete
                        exercises={availableExercises}
                        value={availableExercises.find(ex => ex.id === exercise.exercise_id) || null}
                        onChange={(selectedExercise) => {
                          if (selectedExercise) {
                            handleExerciseChange(index, 'exercise_id', selectedExercise.id);
                            handleExerciseChange(index, 'exercise_name', selectedExercise.name);
                          } else {
                            handleExerciseChange(index, 'exercise_id', 0);
                            handleExerciseChange(index, 'exercise_name', '');
                          }
                        }}
                        onCreateExercise={handleCreateExercise}
                        label={t('workoutShare.exerciseName')}
                        disabled={loading || loadingExercises}
                      />
                    </Grid>
                    <Grid size={{ xs: 5, sm: 3 }}>
                      <TextField
                        fullWidth
                        type="number"
                        label={t('workoutShare.sets')}
                        value={exercise.sets}
                        onChange={(e) => handleExerciseChange(index, 'sets', parseInt(e.target.value) || 1)}
                        inputProps={{ min: 1, max: 20 }}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid size={{ xs: 5, sm: 3 }}>
                      <TextField
                        fullWidth
                        type="number"
                        label={t('workoutShare.reps')}
                        value={exercise.reps}
                        onChange={(e) => handleExerciseChange(index, 'reps', parseInt(e.target.value) || 1)}
                        inputProps={{ min: 1, max: 100 }}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid size={{ xs: 2, sm: 1 }}>
                      <IconButton
                        onClick={() => handleRemoveExercise(index)}
                        disabled={exerciseInputs.length === 1 || loading}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Paper>
              ))}

              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddExercise}
                disabled={loading}
              >
                {t('workoutShare.addExercise')}
              </Button>

              {/* Optional message */}
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('workoutShare.message')}
                placeholder={t('workoutShare.messagePlaceholder')}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={loading}
              />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t('workoutShare.cancel')}
        </Button>
        {acceptedFriends.length > 0 && (
          <Button
            onClick={handleShare}
            variant="contained"
            disabled={loading || !selectedFriendId || !validateExercises()}
          >
            {loading ? t('common.sharing') : t('workoutShare.share')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ShareWorkoutDialog;
